import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../core/store';
import { 
  clearError, 
  clearSuccess, 
  logout,
} from '../../../core/store/slices/auth.slice';
import { clearAllApiErrors, clearApiError } from '../../../core/store/slices/apiError.slice';
import { ErrorSource } from '../../../core/constant/model/api/api';
import { 
  useMemberSignupMutation, 
  useSendVerificationCodeMutation, 
  useVerifyMFACodeMutation, 
  useVerifyPhoneMutation,
  useSendEmailVerificationMutation,
  useCompleteSignUpVerificationMutation
} from './authApi';
import { 
  MemberSignupRequest, 
  SendVerificationCodeRequest, 
  VerifyCodeRequest, 
  SendEmailVerificationRequest,
  CompleteSignUpRequest
} from '../types/account';
import { useProfileQuery } from './useProfileQuery';
import { clearBasicInfo } from '../../../core/store/slices/basicInfoSlice';
import { clearPersonalDetails } from '../../../core/store/slices/personalDetailsSlice';
import { clearMemberSubscription } from '../../../core/store/slices/memberSubscriptionSlice';

const phoneUtils = {
  format: (input: string): string => {
    const digitsOnly = input.replace(/\D/g, '');
    
    if (digitsOnly.length <= 3) {
      return digitsOnly;
    } else if (digitsOnly.length <= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
  },
  
  parse: (input: string): string => {
    return input.replace(/\D/g, '');
  },
  
  withCountryCode: (phoneNumber: string): string => {
    const digits = phoneUtils.parse(phoneNumber);
    return digits.startsWith('1') ? `+${digits}` : `+1${digits}`;
  }
};

export const useAuth = () => {  
  const auth = useSelector((state: RootState) => state.auth);
  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const personalDetails = useSelector((state: RootState) => state.personalDetails);
  const preferenceInformation = useSelector((state: RootState) => state.preferenceInformation);
  const emergencyContact = useSelector((state: RootState) => state.emergencyContact);
  const availability = useSelector((state: RootState) => state.availability);
  const apiErrors = useSelector((state: RootState) => state.apiError?.errors || {});
  
  const dispatch = useDispatch();
  
  const sendVerificationCodeMutation = useSendVerificationCodeMutation();
  const verifyMFACodeMutation = useVerifyMFACodeMutation();
  const memberSignupMutation = useMemberSignupMutation();
  const verifyPhoneMutation = useVerifyPhoneMutation();
  const sendEmailVerificationMutation = useSendEmailVerificationMutation();
  const completeSignUpVerificationMutation = useCompleteSignUpVerificationMutation();

  const profileQuery = useProfileQuery(false);
  
  useEffect(() => {
    const shouldFetchProfile = auth.isAuthenticated && auth.token && !basicInfo?._id;
    
    if (shouldFetchProfile) {
      profileQuery.refetch();
    }
  }, [auth.isAuthenticated, auth.token, basicInfo?._id, profileQuery]);
  
  const handleLogout = useCallback(() => {
    setTimeout(() => {
      dispatch(clearBasicInfo())
      dispatch(clearPersonalDetails());
      dispatch(clearMemberSubscription())
      dispatch(clearAllApiErrors());
      dispatch(clearSuccess());
      dispatch(clearError());
      dispatch(logout());      
      localStorage.clear();
      window.location.reload();
    }, 10);
  }, [dispatch]);
  
  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);
  
  const handleClearSuccess = useCallback(() => {
    dispatch(clearSuccess());
  }, [dispatch]);

  const clearAuthApiError = useCallback((source: ErrorSource) => {
    dispatch(clearApiError(source));
  }, [dispatch]);

  const clearAllAuthApiErrors = useCallback(() => {
    dispatch(clearApiError(ErrorSource.AUTHENTICATION));
    dispatch(clearApiError(ErrorSource.VERIFY_CODE));
    dispatch(clearApiError(ErrorSource.SEND_CODE_VERIFICATION));
    dispatch(clearApiError(ErrorSource.MEMBER_SIGNUP));
    dispatch(clearApiError(ErrorSource.SEND_EMAIL_VERIFICATION));
    dispatch(clearApiError(ErrorSource.COMPLETE_SIGNUP));
  }, [dispatch]);
  
  const handleSendVerificationCode = useCallback(async (request: SendVerificationCodeRequest) => {
    dispatch(clearApiError(ErrorSource.SEND_CODE_VERIFICATION));
    dispatch(clearError());
    
    try {
      return await sendVerificationCodeMutation.mutateAsync(request);
    } catch (error) {
      console.error('Error in handleSendVerificationCode:', error);
      throw error;
    }
  }, [dispatch, sendVerificationCodeMutation]);

  const handleVerifyMFACode = useCallback(async (request: VerifyCodeRequest) => {
    dispatch(clearApiError(ErrorSource.VERIFY_CODE));
    dispatch(clearError());
    
    try {
      return await verifyMFACodeMutation.mutateAsync(request);
    } catch (error) {
      console.error('Error in handleVerifyMFACode:', error);
      throw error;
    }
  }, [dispatch, verifyMFACodeMutation]);

  const handleMemberSignup = useCallback(async (request: MemberSignupRequest) => {
    dispatch(clearApiError(ErrorSource.MEMBER_SIGNUP));
    dispatch(clearError());
    
    try {
      return await memberSignupMutation.mutateAsync(request);
    } catch (error) {
      console.error('Error in handleMemberSignup:', error);
      throw error;
    }
  }, [dispatch, memberSignupMutation]);

  const handleVerifyPhone = useCallback(async (request: VerifyCodeRequest) => {
    dispatch(clearApiError(ErrorSource.VERIFY_CODE));
    dispatch(clearError());
    
    try {
      return await verifyPhoneMutation.mutateAsync(request);
    } catch (error) {
      console.error('Error in handleVerifyPhone:', error);
      throw error;
    }
  }, [dispatch, verifyPhoneMutation]);

  const handleSendEmailVerification = useCallback(async (request: SendEmailVerificationRequest) => {
    dispatch(clearApiError(ErrorSource.SEND_EMAIL_VERIFICATION));
    dispatch(clearError());
    
    try {
      return await sendEmailVerificationMutation.mutateAsync(request);
    } catch (error) {
      console.error('Error in handleSendEmailVerification:', error);
      throw error;
    }
  }, [dispatch, sendEmailVerificationMutation]);

  const handleCompleteSignUpVerification = useCallback(async (request: CompleteSignUpRequest) => {
    dispatch(clearApiError(ErrorSource.COMPLETE_SIGNUP));
    dispatch(clearError());
    
    try {
      return await completeSignUpVerificationMutation.mutateAsync(request);
    } catch (error) {
      console.error('Error in handleCompleteSignUpVerification:', error);
      throw error;
    }
  }, [dispatch, completeSignUpVerificationMutation]);

  const getApiErrorBySource = useCallback((source: ErrorSource) => {
    return apiErrors[source];
  }, [apiErrors]);
  
  const hasApiError = useCallback((source: ErrorSource) => {
    return !!apiErrors[source];
  }, [apiErrors]);
  
  const apiErrorContains = useCallback((source: ErrorSource, text: string) => {
    const error = apiErrors[source];
    return !!error && error.message.includes(text);
  }, [apiErrors]);
  
  return {
    // Auth state
    isAuthenticated: auth.isAuthenticated,
    token: auth.token,
    tokenExpiresAt: auth.tokenExpiresAt,
    userId: auth.userId,
    basicInfo,
    personalDetails,
    preferenceInformation,
    emergencyContact,
    availability,
    verification: auth.verification,
    isLoading: auth.isLoading,
    isSendingCode: auth.isSendingCode,
    isVerifyingCode: auth.isVerifyingCode,
    error: auth.error,
    success: auth.success,
    
    // Profile loading state
    isLoadingProfile: profileQuery.isLoading,
    refreshProfile: useCallback(() => {
      if (auth.isAuthenticated && auth.token) {
        return profileQuery.refetch();
      }
      return Promise.resolve(null);
    }, [auth.isAuthenticated, auth.token, profileQuery]),
    
    // Error functions
    getApiErrorBySource,
    hasApiError,
    apiErrorContains,
    
    // Action functions
    logout: handleLogout,
    clearError: handleClearError,
    clearSuccess: handleClearSuccess,
    clearApiError: clearAuthApiError,
    clearAllApiErrors: clearAllAuthApiErrors,
    
    // API functions
    sendVerificationCode: handleSendVerificationCode,
    verifyMFACode: handleVerifyMFACode,
    sendMemberSignup: handleMemberSignup,
    verifyPhone: handleVerifyPhone,
    sendEmailVerification: handleSendEmailVerification,
    completeSignUpVerification: handleCompleteSignUpVerification,
    
    // Utilities
    phoneUtils
  };
};